import { decamelizeKeys } from 'humps';

import { apiClient } from '../apiClient';

export const PATHS = {
  HISTORICAL_METRIC_POINTS: '/api/v3/historicalmetricpoints',
} as const;

type RawPoint = { date: string; value: number };

type HistoricalMetricPointWire = {
  metric?: { key?: string };
  historicalData?: { series?: { rawPoints?: RawPoint[] } };
};

type HistoricalMetricPointsResponse = { objects?: HistoricalMetricPointWire[] };

/**
 * The most-recent value for each metric key (e.g. 'phq-8', 'gad-2',
 * 'prediabetes-risk'). Mirrors Sparta's historicalMetricPoint schema: per
 * metric, sort raw points by date and keep the latest value. Used to decide
 * whether the onboarding survey has already been answered (see isSurveyComplete).
 */
export async function fetchHistoricalMetricPoints(
  signal?: AbortSignal
): Promise<Record<string, number>> {
  const res = await apiClient.get<HistoricalMetricPointsResponse>(PATHS.HISTORICAL_METRIC_POINTS, {
    params: decamelizeKeys({ metricKey: 'all' }),
    signal,
  });

  const values: Record<string, number> = {};
  for (const point of res.data?.objects ?? []) {
    const key = point.metric?.key;
    const rawPoints = point.historicalData?.series?.rawPoints ?? [];
    if (!key || rawPoints.length === 0) continue;
    const latest = rawPoints.reduce((a, b) =>
      new Date(a.date).getTime() >= new Date(b.date).getTime() ? a : b
    );
    values[key] = latest.value;
  }
  return values;
}
